import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { searchEntitiesByKeyword } from '@prism/prism-shared';
import { BehaviorSubject, Subject, takeUntil, tap } from 'rxjs';
import { EntitySelectOption } from './entity-select-option.model';

/**
 * The half-way house between the two controls we already had, neither of which can be used as a
 * filter:
 *
 *   - prism-entity-search-form has the row we want (image / name / description, match highlighting,
 *     arrow-key navigation) but selecting an option EMITS AN ID AND CLEARS ITSELF. It never renders
 *     what you picked, so it can only ever be a "jump to" control.
 *   - prism-select holds a selection and renders it with a clear button, but its SelectOption is
 *     {label, value} — no image, no description — so a part number arrives with no context.
 *
 * This is select's value-holding shell wrapped around entity-search's option row. It carries its own
 * EntitySelectOption, identical to the search form's today and free to diverge from it tomorrow.
 */
@Component({
  selector: 'prism-entity-select',
  templateUrl: './entity-select.component.html',
  styleUrls: ['./entity-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EntitySelectComponent),
      multi: true,
    },
  ],
})
export class EntitySelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  @Input() options: EntitySelectOption[] | null = [];
  @Input() prompt: string = 'select';
  /** shown in place of the list when the keyword matches nothing */
  @Input() noResultsText: string = 'no matches';
  @Input() defaultImageUrl!: string;
  @Input() disableUnselect: boolean = false;
  @Input() disable: boolean = false;
  @Input() openAbove: boolean = false;
  /** How many options to show before anything is typed. 0 (or less) shows all of them — which is
      what a filter wants: a list silently cut at ten is read as the list, not as a page of it. */
  @Input() maximumUnfilteredOptions: number = 10;
  @Input() value: string | null = null;

  @Output() changed: EventEmitter<string | null> = new EventEmitter();

  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  public searchControl = new FormControl('');
  public filteredOptions$ = new BehaviorSubject<EntitySelectOption[]>([]);
  public highlightedOptionIndex$ = new BehaviorSubject<number | null>(null);
  public isOpen: boolean = false;
  public selectedOption: EntitySelectOption | null = null;

  private onChanged: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.resolveSelection();

    this.searchControl.valueChanges.pipe(
      tap((keyword) => this.applyKeyword(keyword)),
      takeUntil(this.unsubscribe$),
    ).subscribe();
  }

  /* Options usually arrive after the first render — they are derived from data still loading — so
     the selection is re-resolved whenever they change, otherwise a value written before the list
     existed would render as an empty field. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['value']) {
      this.resolveSelection();
      if (this.isOpen) {
        this.applyKeyword(this.searchControl.value);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // ----- ControlValueAccessor -----

  public writeValue(value: string | null): void {
    this.value = value ?? null;
    this.resolveSelection();
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChanged = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
  }

  // ----- interaction -----

  public open(): void {
    if (this.disable || this.isOpen) {
      return;
    }
    this.isOpen = true;
    /* The field turns into a text input only while it is open, so the input does not exist yet on
       this frame — focus it once the view has been rewritten, or the user has to click twice. */
    this.searchControl.setValue('', { emitEvent: false });
    this.applyKeyword('');
    setTimeout(() => this.searchInputRef?.nativeElement.focus());
  }

  public close(): void {
    this.isOpen = false;
    this.highlightedOptionIndex$.next(null);
    this.onTouched();
  }

  public selectOption(option: EntitySelectOption): void {
    this.setValue(option.id);
    this.close();
  }

  public clearSelection(): void {
    this.setValue(null);
    this.close();
  }

  public trackByOptionId(_index: number, option: EntitySelectOption): string {
    return option.id;
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.isOpen) {
      return;
    }
    const options = this.filteredOptions$.value;
    const index = this.highlightedOptionIndex$.value;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlight(index === null ? 0 : Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlight(index === null ? options.length - 1 : Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && index !== null && options[index]) {
      event.preventDefault();
      this.selectOption(options[index]);
    }
  }

  private highlight(index: number): void {
    this.highlightedOptionIndex$.next(index);
    document.getElementById(`entity_select_option_${index}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  private setValue(value: string | null): void {
    this.value = value;
    this.resolveSelection();
    this.onChanged(value);
    this.changed.emit(value);
  }

  private resolveSelection(): void {
    this.selectedOption = this.value
      ? (this.options ?? []).find(option => option.id === this.value) ?? null
      : null;
  }

  private applyKeyword(keyword: string | null): void {
    const options = this.options ?? [];
    if (!keyword?.trim()) {
      this.filteredOptions$.next(
        this.maximumUnfilteredOptions > 0 ? options.slice(0, this.maximumUnfilteredOptions) : options,
      );
    } else {
      const fields = options.map(option => [option.name, option.description]);
      this.filteredOptions$.next(searchEntitiesByKeyword<EntitySelectOption>(keyword, options, fields));
    }
    this.highlightedOptionIndex$.next(null);
  }
}
